import { useMemo, useState } from "react";
import {
	Box,
	Button,
	ButtonGroup,
	Flex,
	Grid,
	GridItem,
	HStack,
	Heading,
	Icon,
	SimpleGrid,
	Spinner,
	Text,
	VStack,
} from "@chakra-ui/react";
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Line,
	LineChart,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import {
	BiBarChartAlt2,
	BiCheckCircle,
	BiTime,
	BiCalendarAlt,
	BiTrendingUp,
	BiBug,
	BiRefresh,
} from "react-icons/bi";
import { format, startOfMonth, subMonths, subDays, subYears, getDay } from "date-fns";
import { es as esLocale, enUS as enLocale } from "date-fns/locale";

import { TemplatePage } from "components/layout/template";
import { useLangContext } from "configs/languageContext";
import { useDataContext } from "configs/dataContext";
import { dashboard, form_fields, translation } from "data/translations";

// ---------- palette ----------
const C = {
	primary: "#059669",      // emerald-600
	primaryLight: "#ECFDF5", // emerald-50
	primaryRing: "#047857",  // emerald-700
	charcoal: "#0F172A",     // slate-900
	sand: "#F8FAFC",         // slate-50
	sandWarm: "#F1F5F9",     // slate-100
	ink: "#0F172A",
	muted: "#64748B",        // slate-500
	border: "#E2E8F0",       // slate-200
	gridLine: "#E2E8F0",
};

// Coordinated chart palette (modern, friendly to colorblind-ish, distinct).
const TYPE_COLORS = [
	"#059669", "#F59E0B", "#0EA5E9", "#8B5CF6",
	"#EC4899", "#14B8A6", "#F97316", "#64748B",
];

const CONDITION_COLORS: Record<string, string> = {
	dead: "#DC2626",      // red-600
	alive: "#059669",     // emerald-600
	injured: "#F59E0B",   // amber-500
	other: "#94A3B8",     // slate-400
};

type RangeKey = "3m" | "6m" | "12m" | "24m" | "5y" | "all";
const RANGE_ORDER: RangeKey[] = ["3m", "6m", "12m", "24m", "5y", "all"];

function rangeStart(key: RangeKey, now: Date): Date | null {
	switch (key) {
		case "3m":  return startOfMonth(subMonths(now, 2));
		case "6m":  return startOfMonth(subMonths(now, 5));
		case "12m": return startOfMonth(subMonths(now, 11));
		case "24m": return startOfMonth(subMonths(now, 23));
		case "5y":  return startOfMonth(subYears(now, 5));
		case "all": return null;
	}
}
function rangeMonths(key: RangeKey): number {
	switch (key) {
		case "3m":  return 3;
		case "6m":  return 6;
		case "12m": return 12;
		case "24m": return 24;
		case "5y":  return 60;
		case "all": return 999;
	}
}

function toDate(ts: any): Date | null {
	if (!ts) return null;
	if (ts instanceof Date) return ts;
	if (typeof ts.toDate === "function") return ts.toDate();
	const seconds = ts.seconds ?? ts._seconds;
	if (typeof seconds === "number") return new Date(seconds * 1000);
	return null;
}

function tally<T extends { [k: string]: any }>(rows: T[], field: string) {
	const m = new Map<string, number>();
	for (const r of rows) {
		const v = r[field];
		if (v == null || v === "") continue;
		m.set(String(v), (m.get(String(v)) ?? 0) + 1);
	}
	return Array.from(m.entries())
		.map(([label, value]) => ({ label, value }))
		.sort((a, b) => b.value - a.value);
}

const EmployeeDashboardPage = () => {
	const { lang } = useLangContext();
	const { data, loading, lastRefresh, getData } = useDataContext();
	const dateLocale = lang === "es" ? esLocale : enLocale;
	const [range, setRange] = useState<RangeKey>("all");

	const t = (key: string) => dashboard[key]?.[lang] ?? key;
	const labelType = (k: string) =>
		dashboard[`type_${k}`]?.[lang]
		?? form_fields.accident_type?.[k]?.[lang]
		?? form_fields.accident_type?.[k]?.es
		?? k;
	const labelSpecies = (k: string) =>
		dashboard[`species_${k}`]?.[lang]
		?? form_fields.animal_species?.[k]?.[lang]
		?? form_fields.animal_species?.[k]?.es
		?? k;
	const labelCondition = (k: string) =>
		dashboard[`condition_${k}`]?.[lang]
		?? form_fields.animal_condition?.[k]?.[lang]
		?? form_fields.animal_condition?.[k]?.es
		?? k;

	const enriched = useMemo(
		() => (data || []).map((a: any) => ({ ...a, _date: toDate(a.accident_timestamp) })),
		[data]
	);

	const now = useMemo(() => new Date(), []);
	const monthStart = startOfMonth(now);
	const sevenDaysAgo = subDays(now, 7);
	const thirtyDaysAgo = subDays(now, 30);

	const total = enriched.length;
	const approved = enriched.filter((a: any) => a.validation_status === "approved").length;
	const pending = enriched.filter(
		(a: any) => a.validation_status && a.validation_status !== "approved"
	).length;
	const thisMonth = enriched.filter((a: any) => a._date && a._date >= monthStart).length;
	const last7 = enriched.filter((a: any) => a._date && a._date >= sevenDaysAgo).length;
	const last30 = enriched.filter((a: any) => a._date && a._date >= thirtyDaysAgo).length;

	const rangeStartDate = useMemo(() => rangeStart(range, now), [range, now]);
	const filtered = useMemo(() => {
		if (!rangeStartDate) return enriched;
		return enriched.filter((a: any) => a._date && a._date >= rangeStartDate);
	}, [enriched, rangeStartDate]);
	const filteredCount = filtered.length;

	const byType      = useMemo(() => tally(filtered, "accident_type"), [filtered]);
	const bySpecies   = useMemo(() => tally(filtered, "animal_species"), [filtered]);
	const byCondition = useMemo(() => tally(filtered, "animal_condition"), [filtered]);

	const monthly = useMemo(() => {
		const months = Math.min(rangeMonths(range), 60);
		const start = rangeStartDate ?? startOfMonth(subYears(now, 10));
		const buckets = new Map<string, number>();
		const monthCount = rangeStartDate
			? months
			: Math.max(1, Math.round((now.getTime() - start.getTime()) / (30 * 24 * 3600 * 1000)));
		for (let i = monthCount - 1; i >= 0; i--) {
			const d = startOfMonth(subMonths(now, i));
			if (rangeStartDate && d < rangeStartDate) continue;
			buckets.set(d.toISOString(), 0);
		}
		for (const a of enriched) {
			if (!a._date) continue;
			if (rangeStartDate && a._date < rangeStartDate) continue;
			const k = startOfMonth(a._date).toISOString();
			if (buckets.has(k)) buckets.set(k, (buckets.get(k) ?? 0) + 1);
		}
		return Array.from(buckets.entries()).map(([iso, value]) => ({
			date: new Date(iso),
			label: format(new Date(iso), "MMM yy", { locale: dateLocale }),
			value,
		}));
	}, [enriched, range, rangeStartDate, now, dateLocale]);

	const peakMonth = monthly.reduce(
		(p, c) => (c.value > p.value ? c : p),
		monthly[0] ?? { date: now, label: "", value: 0 }
	);

	// Year-over-year: monthly count for current year vs prior year
	const yoy = useMemo(() => {
		const curY = now.getFullYear();
		const prevY = curY - 1;
		const buckets = Array.from({ length: 12 }, (_, m) => ({
			month: m,
			label: format(new Date(2000, m, 1), "LLL", { locale: dateLocale }),
			current: 0,
			prior: 0,
		}));
		for (const a of enriched) {
			if (!a._date) continue;
			const y = a._date.getFullYear();
			const m = a._date.getMonth();
			if (y === curY) buckets[m].current += 1;
			else if (y === prevY) buckets[m].prior += 1;
		}
		return { rows: buckets, curY, prevY };
	}, [enriched, now, dateLocale]);

	// Day of week distribution (Mon-Sun)
	const dow = useMemo(() => {
		// JS: 0=Sun … 6=Sat. Reorder to Mon=0..Sun=6.
		const counts = [0, 0, 0, 0, 0, 0, 0];
		for (const a of filtered) {
			if (!a._date) continue;
			const js = getDay(a._date); // 0=Sun..6=Sat
			const i = (js + 6) % 7; // → 0=Mon..6=Sun
			counts[i] += 1;
		}
		const labels = [0, 1, 2, 3, 4, 5, 6].map((i) => {
			// pick a known Mon..Sun and format with locale
			const ref = new Date(2024, 0, 1 + i); // 2024-01-01 is Monday
			return format(ref, "EEE", { locale: dateLocale });
		});
		return counts.map((value, i) => ({ label: labels[i], value }));
	}, [filtered, dateLocale]);

	// Transportation outcomes
	const transport = useMemo(() => {
		const m = new Map<string, number>();
		for (const a of filtered) {
			const v = a.transportation_performed;
			let bucket = "transport_none";
			if (v === "alive") bucket = "transport_alive";
			else if (v === "dead" || v === "deceased") bucket = "transport_dead";
			else if (v && v !== "none") bucket = "transport_other";
			m.set(bucket, (m.get(bucket) ?? 0) + 1);
		}
		const COLORS: Record<string, string> = {
			transport_alive: "#059669",
			transport_dead: "#DC2626",
			transport_other: "#F59E0B",
			transport_none: "#94A3B8",
		};
		return Array.from(m.entries()).map(([key, value]) => ({
			name: t(key),
			value,
			color: COLORS[key],
		}));
	}, [filtered, lang]); // eslint-disable-line react-hooks/exhaustive-deps

	const typePie = byType.slice(0, 7).map((s, i) => ({
		name: labelType(s.label),
		value: s.value,
		color: TYPE_COLORS[i] ?? "#64748B",
	}));

	const conditionPie = byCondition.slice(0, 5).map((s) => ({
		name: labelCondition(s.label),
		value: s.value,
		color: CONDITION_COLORS[s.label] ?? "#94A3B8",
	}));

	const speciesBars = bySpecies
		.slice(0, 8)
		.map((s) => ({ name: labelSpecies(s.label), value: s.value }));

	const topSpeciesLabel = bySpecies[0] ? labelSpecies(bySpecies[0].label) : "—";
	const topSpeciesCount = bySpecies[0]?.value ?? 0;

	const lastRefreshDate = lastRefresh ? new Date(lastRefresh) : null;
	const lastRefreshFmt = lastRefreshDate
		? format(lastRefreshDate, "PP p", { locale: dateLocale })
		: null;

	if (loading && total === 0) {
		return (
			<TemplatePage sidebar>
				<Flex justify="center" align="center" py={20}>
					<Spinner size="xl" mr={4} color={C.primary} />
					<Text>{t("loading_reports")}</Text>
				</Flex>
			</TemplatePage>
		);
	}

	if (total === 0) {
		return (
			<TemplatePage sidebar>
				<Text textAlign="center" py={20} color="gray.500">{t("no_data")}</Text>
			</TemplatePage>
		);
	}

	return (
		<TemplatePage sidebar>
			<Box
				w="100%"
				bgGradient={`linear(to-b, ${C.sand} 0%, white 220px)`}
				borderRadius={{ base: "xl", md: "2xl" }}
				p={{ base: 3, md: 6 }}
			>
				<VStack spacing={{ base: 4, md: 7 }} align="stretch" w="100%">

					{/* Hero header */}
					<Flex
						justify="space-between"
						align={{ base: "flex-start", md: "flex-end" }}
						direction={{ base: "column", md: "row" }}
						gap={{ base: 3, md: 4 }}
					>
						<Box>
							<Text
								fontSize="xs"
								color={C.primary}
								fontWeight="700"
								textTransform="uppercase"
								letterSpacing="0.18em"
								mb={1}
							>
								SalveMonos · MonoSOS
							</Text>
							<Heading
								as="h1"
								fontSize={{ base: "2xl", sm: "3xl", md: "4xl" }}
								fontWeight="800"
								letterSpacing="-0.025em"
								color={C.ink}
								lineHeight="1.05"
							>
								{translation.emp_selector_vis[lang]}
							</Heading>
							<Text color={C.muted} fontSize="sm" mt={2}>
								{total.toLocaleString()} {t("reports_word")} · {approved.toLocaleString()} {t("status_approved")}
							</Text>
						</Box>
						<HStack spacing={3} align="center">
							{lastRefreshFmt && (
								<Text fontSize="xs" color={C.muted} display={{ base: "none", lg: "block" }}>
									{t("last_updated")}: <Text as="span" fontWeight="700" color={C.ink}>{lastRefreshFmt}</Text>
								</Text>
							)}
							<Button
								size="sm"
								onClick={() => getData()}
								isLoading={loading}
								leftIcon={<BiRefresh size="18" />}
								bg={C.primary}
								color="white"
								_hover={{ bg: C.primaryRing }}
								borderRadius="full"
								px={4}
								minH="40px"
							>
								{loading ? t("refreshing") : t("refresh")}
							</Button>
						</HStack>
					</Flex>

					{/* Range bar */}
					<Box
						bg="white"
						p={{ base: 2, md: 3 }}
						borderRadius={{ base: "xl", md: "full" }}
						borderWidth="1px"
						borderColor={C.border}
						boxShadow="0 1px 3px rgba(50,60,70,0.04)"
					>
						<Flex
							justify="space-between"
							align={{ base: "stretch", sm: "center" }}
							direction={{ base: "column", sm: "row" }}
							gap={{ base: 2, sm: 3 }}
						>
							<HStack
								spacing={{ base: 2, md: 3 }}
								pl={{ base: 1, md: 2 }}
								overflowX="auto"
								css={{ scrollbarWidth: "none", "::-webkit-scrollbar": { display: "none" } }}
							>
								<Text
									fontSize="xs"
									color={C.muted}
									fontWeight="700"
									textTransform="uppercase"
									letterSpacing="0.1em"
									display={{ base: "none", md: "block" }}
									flexShrink={0}
								>
									{t("range_label")}
								</Text>
								<ButtonGroup size="sm" isAttached variant="ghost" flexShrink={0}>
									{RANGE_ORDER.map((k) => (
										<Button
											key={k}
											onClick={() => setRange(k)}
											bg={range === k ? C.primary : "transparent"}
											color={range === k ? "white" : C.charcoal}
											_hover={range === k ? { bg: C.primaryRing } : { bg: C.primaryLight, color: C.primary }}
											borderRadius="full"
											fontWeight={range === k ? "700" : "500"}
											fontSize="xs"
											px={3}
											minH="36px"
										>
											{t(`range_${k}`)}
										</Button>
									))}
								</ButtonGroup>
							</HStack>
							<HStack spacing={2} fontSize="sm" pr={3}>
								<Text fontWeight="800" color={C.primary} fontSize="md">
									{filteredCount.toLocaleString()}
								</Text>
								<Text color={C.muted}>{t("range_in_view")}</Text>
							</HStack>
						</Flex>
					</Box>

					{/* KPI cards */}
					<SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3}>
						<KPI label={t("kpi_total")}    value={total.toLocaleString()}    accent="primary" icon={BiBarChartAlt2} />
						<KPI label={t("kpi_approved")} value={approved.toLocaleString()} accent="green" icon={BiCheckCircle}
							helpText={`${Math.round((approved / Math.max(total, 1)) * 100)}% ${t("kpi_pct_of_total")}`}/>
						<KPI label={t("kpi_pending")}  value={pending.toLocaleString()}  accent="amber" icon={BiTime} />
						<KPI label={t("kpi_this_month")} value={thisMonth.toLocaleString()} accent="indigo" icon={BiCalendarAlt}
							helpText={format(monthStart, "MMM yyyy", { locale: dateLocale })}/>
						<KPI label={t("kpi_last_7")}   value={last7.toLocaleString()} accent="teal" icon={BiTrendingUp}
							helpText={`${last30} ${t("kpi_last_30")}`}/>
						<KPI label={t("kpi_top_species")} value={topSpeciesLabel} accent="violet" icon={BiBug} compact
							helpText={`${topSpeciesCount} ${t("reports_word")}`}/>
					</SimpleGrid>

					{/* Monthly + condition */}
					<Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={4}>
						<GridItem>
							<ChartCard
								title={t("chart_monthly")}
								subtitle={`${t("chart_monthly_sub")} · ${t("range_label")}: ${peakMonth.value} (${peakMonth.label})`}
							>
								<Box h={{ base: "220px", md: "280px" }}>
									<ResponsiveContainer width="100%" height="100%">
										<AreaChart data={monthly} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
											<defs>
												<linearGradient id="grad-monthly" x1="0" y1="0" x2="0" y2="1">
													<stop offset="0%" stopColor={C.primary} stopOpacity={0.45} />
													<stop offset="100%" stopColor={C.primary} stopOpacity={0.02} />
												</linearGradient>
											</defs>
											<CartesianGrid stroke={C.gridLine} vertical={false} />
											<XAxis dataKey="label" tick={{ fontSize: 11, fill: C.muted }} stroke={C.gridLine} tickLine={false} axisLine={false} />
											<YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.muted }} stroke={C.gridLine} tickLine={false} axisLine={false} />
											<Tooltip content={<RTooltip unitKey={t("reports_word")} />} cursor={{ stroke: C.charcoal, strokeDasharray: "4 4" }} />
											<Area type="monotone" dataKey="value" stroke={C.primary} strokeWidth={2.5} fill="url(#grad-monthly)" />
										</AreaChart>
									</ResponsiveContainer>
								</Box>
							</ChartCard>
						</GridItem>
						<GridItem>
							<ChartCard title={t("chart_condition")} subtitle={t("chart_condition_sub")}>
								<DonutChart data={conditionPie} centerValue={filteredCount.toLocaleString()} centerLabel={t("reports_word")} />
							</ChartCard>
						</GridItem>
					</Grid>

					{/* YoY + DoW */}
					<Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={4}>
						<GridItem>
							<ChartCard
								title={t("chart_yoy")}
								subtitle={`${yoy.prevY} vs ${yoy.curY}`}
							>
								<Box h={{ base: "220px", md: "260px" }}>
									<ResponsiveContainer width="100%" height="100%">
										<LineChart data={yoy.rows} margin={{ top: 12, right: 12, left: -12, bottom: 0 }}>
											<CartesianGrid stroke={C.gridLine} vertical={false} />
											<XAxis dataKey="label" tick={{ fontSize: 11, fill: C.muted }} stroke={C.gridLine} tickLine={false} axisLine={false} />
											<YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.muted }} stroke={C.gridLine} tickLine={false} axisLine={false} />
											<Tooltip content={<RTooltip />} />
											<Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
											<Line type="monotone" dataKey="prior" name={`${t("year_prior")} (${yoy.prevY})`} stroke="#94A3B8" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
											<Line type="monotone" dataKey="current" name={`${t("year_current")} (${yoy.curY})`} stroke={C.primary} strokeWidth={2.5} dot={{ r: 2.5 }} activeDot={{ r: 6 }} />
										</LineChart>
									</ResponsiveContainer>
								</Box>
							</ChartCard>
						</GridItem>
						<GridItem>
							<ChartCard title={t("chart_dow")} subtitle={t("chart_dow_sub")}>
								<Box h={{ base: "220px", md: "260px" }}>
									<ResponsiveContainer width="100%" height="100%">
										<BarChart data={dow} margin={{ top: 12, right: 8, left: -16, bottom: 0 }}>
											<defs>
												<linearGradient id="grad-dow" x1="0" y1="0" x2="0" y2="1">
													<stop offset="0%" stopColor={C.primary} stopOpacity={0.95} />
													<stop offset="100%" stopColor={C.primary} stopOpacity={0.55} />
												</linearGradient>
											</defs>
											<CartesianGrid stroke={C.gridLine} vertical={false} />
											<XAxis dataKey="label" tick={{ fontSize: 11, fill: C.muted }} stroke={C.gridLine} tickLine={false} axisLine={false} />
											<YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.muted }} stroke={C.gridLine} tickLine={false} axisLine={false} />
											<Tooltip content={<RTooltip unitKey={t("reports_word")} />} cursor={{ fill: C.sandWarm, opacity: 0.4 }} />
											<Bar dataKey="value" fill="url(#grad-dow)" radius={[8, 8, 0, 0]} />
										</BarChart>
									</ResponsiveContainer>
								</Box>
							</ChartCard>
						</GridItem>
					</Grid>

					{/* Types + species */}
					<Grid templateColumns={{ base: "1fr", lg: "1fr 1fr" }} gap={4}>
						<GridItem>
							<ChartCard title={t("chart_types")} subtitle={t("chart_types_sub")}>
								<DonutChart data={typePie} centerValue={typePie[0]?.name ?? "—"} centerLabel="" centerCompact />
							</ChartCard>
						</GridItem>
						<GridItem>
							<ChartCard title={t("chart_species")} subtitle={t("chart_species_sub")}>
								<HBars data={speciesBars} />
							</ChartCard>
						</GridItem>
					</Grid>

					{/* Transportation outcomes — full width */}
					<ChartCard title={t("chart_transport")} subtitle={t("chart_transport_sub")}>
						<DonutChart
							data={transport.filter((x) => x.value > 0)}
							centerValue={filteredCount.toLocaleString()}
							centerLabel={t("reports_word")}
						/>
					</ChartCard>

				</VStack>
			</Box>
		</TemplatePage>
	);
};

// ---------- accent palette for KPI cards ----------
const ACCENT: Record<string, { ring: string; iconBg: string; iconColor: string; numColor: string }> = {
	primary: { ring: C.primary,  iconBg: C.primaryLight, iconColor: C.primary,  numColor: C.ink },
	green:   { ring: "#10B981",  iconBg: "#D1FAE5",      iconColor: "#047857",  numColor: C.ink },
	amber:   { ring: "#F59E0B",  iconBg: "#FFFBEB",      iconColor: "#B45309",  numColor: C.ink },
	indigo:  { ring: "#6366F1",  iconBg: "#EEF2FF",      iconColor: "#4338CA",  numColor: C.ink },
	teal:    { ring: "#14B8A6",  iconBg: "#F0FDFA",      iconColor: "#0F766E",  numColor: C.ink },
	violet:  { ring: "#8B5CF6",  iconBg: "#F5F3FF",      iconColor: "#6D28D9",  numColor: C.ink },
};

interface KPIProps {
	label: string;
	value: string | number;
	helpText?: string;
	accent?: string;
	compact?: boolean;
	icon?: any;
}
const KPI = ({ label, value, helpText, accent = "primary", compact = false, icon: IconC }: KPIProps) => {
	const a = ACCENT[accent] ?? ACCENT.primary;
	return (
		<Box
			position="relative"
			bg="white"
			borderRadius="xl"
			px={{ base: 3, md: 4 }}
			py={{ base: 2.5, md: 3.5 }}
			boxShadow="0 1px 3px rgba(50,60,70,0.06)"
			borderWidth="1px"
			borderColor={C.border}
			transition="all 0.2s ease"
			_hover={{ transform: "translateY(-2px)", boxShadow: "0 8px 24px rgba(50,60,70,0.08)" }}
			overflow="hidden"
		>
			<Box position="absolute" top={0} left={0} right={0} h="3px" bg={a.ring} opacity={0.85} />
			<Flex justify="space-between" align="flex-start" mb={2} mt={0.5}>
				<Text fontSize="xs" color={C.muted} textTransform="uppercase" letterSpacing="0.08em" fontWeight="700" pr={2}>
					{label}
				</Text>
				{IconC && (
					<Flex bg={a.iconBg} color={a.iconColor} w="30px" h="30px" borderRadius="lg" justify="center" align="center" flexShrink={0}>
						<Icon as={IconC} boxSize={4} />
					</Flex>
				)}
			</Flex>
			<Text
				fontSize={compact ? "lg" : { base: "2xl", md: "3xl" }}
				fontWeight="800"
				color={a.numColor}
				lineHeight="1.05"
				letterSpacing="-0.02em"
				noOfLines={2}
			>
				{value}
			</Text>
			{helpText && (
				<Text fontSize="xs" color={C.muted} mt={1.5} fontWeight="500">
					{helpText}
				</Text>
			)}
		</Box>
	);
};

interface ChartCardProps {
	title: string;
	subtitle?: string;
	children: React.ReactNode;
}
const ChartCard = ({ title, subtitle, children }: ChartCardProps) => (
	<Box
		bg="white"
		borderWidth="1px"
		borderColor={C.border}
		borderRadius="xl"
		p={{ base: 3, md: 5 }}
		h="100%"
		boxShadow="0 1px 3px rgba(50,60,70,0.06)"
		transition="box-shadow 0.2s ease"
		_hover={{ boxShadow: "0 8px 24px rgba(50,60,70,0.08)" }}
	>
		<Heading size="sm" fontWeight="700" letterSpacing="-0.01em" mb={subtitle ? 1 : 4} color={C.ink}>
			{title}
		</Heading>
		{subtitle && (
			<Text fontSize="xs" color={C.muted} mb={4} fontWeight="500">
				{subtitle}
			</Text>
		)}
		{children}
	</Box>
);

// Custom Recharts tooltip
const RTooltip = ({ active, payload, label, unitKey }: any) => {
	if (!active || !payload?.length) return null;
	return (
		<Box bg="white" borderWidth="1px" borderColor={C.border} borderRadius="lg" px={3} py={2} boxShadow="0 6px 18px rgba(50,60,70,0.12)">
			{label !== undefined && (
				<Text fontSize="xs" color={C.muted} fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" mb={1}>{label}</Text>
			)}
			{payload.map((p: any, i: number) => (
				<HStack key={i} fontSize="sm" spacing={2}>
					<Box w="9px" h="9px" borderRadius="full" bg={p.color || p.fill} />
					<Text color={C.charcoal} fontWeight="500">{p.name ?? unitKey ?? ""}</Text>
					<Text color={C.ink} fontWeight="800" ml={1}>{p.value}</Text>
				</HStack>
			))}
		</Box>
	);
};

interface DonutProps {
	data: { name: string; value: number; color: string }[];
	centerValue?: string | number;
	centerLabel?: string;
	centerCompact?: boolean;
}
const DonutChart = ({ data, centerValue, centerLabel, centerCompact }: DonutProps) => (
	<Flex
		direction={{ base: "column", sm: "row" }}
		align="center"
		gap={{ base: 3, sm: 4 }}
		minH={{ base: "auto", sm: "280px" }}
	>
		<Box w={{ base: "100%", sm: "auto" }} flex={{ base: "0 0 auto", sm: "1 1 50%" }} minW={{ base: "auto", sm: "160px" }} h={{ base: "200px", sm: "100%" }} position="relative">
			<ResponsiveContainer width="100%" height="100%">
				<PieChart>
					<Pie
						data={data}
						dataKey="value"
						nameKey="name"
						innerRadius="60%"
						outerRadius="88%"
						paddingAngle={2}
						stroke="white"
						strokeWidth={2}
					>
						{data.map((d, i) => <Cell key={i} fill={d.color} />)}
					</Pie>
					<Tooltip content={<RTooltip />} />
				</PieChart>
			</ResponsiveContainer>
			{centerValue !== undefined && (
				<Box position="absolute" inset={0} display="flex" flexDir="column" justifyContent="center" alignItems="center" pointerEvents="none">
					<Text fontSize={centerCompact ? "sm" : "2xl"} fontWeight="800" textAlign="center" px={2} noOfLines={2} letterSpacing="-0.02em" color={C.ink}>
						{centerValue}
					</Text>
					{centerLabel && <Text fontSize="xs" color={C.muted} fontWeight="500" mt={0.5}>{centerLabel}</Text>}
				</Box>
			)}
		</Box>
		<VStack align="stretch" w={{ base: "100%", sm: "auto" }} flex={{ base: "0 0 auto", sm: "1 1 50%" }} spacing={2}>
			{data.map((d) => {
				const total = data.reduce((a, b) => a + b.value, 0) || 1;
				const pct = ((d.value / total) * 100).toFixed(0);
				return (
					<HStack key={d.name} fontSize="sm" spacing={2}>
						<Box w="10px" h="10px" borderRadius="full" bg={d.color} flexShrink={0} />
						<Text flex="1" noOfLines={1} fontWeight="500" color={C.charcoal}>{d.name}</Text>
						<Text color={C.ink} fontWeight="700" fontSize="xs" sx={{ fontVariantNumeric: "tabular-nums" }}>{d.value}</Text>
						<Text color={C.muted} w="34px" textAlign="right" fontSize="xs" sx={{ fontVariantNumeric: "tabular-nums" }}>{pct}%</Text>
					</HStack>
				);
			})}
		</VStack>
	</Flex>
);

interface HBarsProps {
	data: { name: string; value: number }[];
	barColor?: string;
}
const HBars = ({ data, barColor = C.primary }: HBarsProps) => (
	<Box h={`${Math.max(180, data.length * 32)}px`}>
		<ResponsiveContainer width="100%" height="100%">
			<BarChart data={data} layout="vertical" margin={{ top: 0, right: 24, left: 4, bottom: 0 }}>
				<CartesianGrid stroke={C.gridLine} horizontal={false} />
				<XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: C.muted }} stroke={C.gridLine} tickLine={false} axisLine={false} />
				<YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11, fill: C.charcoal }} stroke={C.gridLine} tickLine={false} axisLine={false} interval={0} />
				<Tooltip content={<RTooltip />} cursor={{ fill: C.sandWarm, opacity: 0.4 }} />
				<Bar dataKey="value" fill={barColor} radius={[0, 6, 6, 0]} />
			</BarChart>
		</ResponsiveContainer>
	</Box>
);

export default EmployeeDashboardPage;
